import { useCallback, useState } from 'react';

type ColorSchema = 'light' | 'dark';

const STORAGE_KEY = 'tps-color-schema';
const ATTR = 'data-ix-color-schema';

function readInitialSchema(): ColorSchema {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored === 'dark' ? 'dark' : 'light';
}

/**
 * Reads/writes the IX color schema (`data-ix-color-schema` on `<html>`),
 * persisted to localStorage. `index.html` sets the initial attribute
 * server-side (light default) so there's no flash-of-wrong-theme; this hook
 * only takes over on user toggle and on mount (to reconcile a stored "dark"
 * preference from a prior visit).
 */
export function useTheme(): { schema: ColorSchema; toggle: () => void } {
  const [schema, setSchema] = useState<ColorSchema>(() => {
    const initial = readInitialSchema();
    document.documentElement.setAttribute(ATTR, initial);
    return initial;
  });

  const toggle = useCallback(() => {
    setSchema(prev => {
      const next: ColorSchema = prev === 'light' ? 'dark' : 'light';
      document.documentElement.setAttribute(ATTR, next);
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  return { schema, toggle };
}
